import { request } from '@umijs/max';
import { buildApiPath } from '@/constants';

/** 服务端容量与保留规则，大小均为字节。 */
export interface DownloadLimits {
  /** 每批业务任务上限。 */
  maxTasks: number;
  /** 每批文件份数上限。 */
  maxFiles: number;
  /** 每批源文件累计字节上限。 */
  maxSourceBytes: number | string;
  /** 每用户排队和执行中任务合计上限。 */
  maxUserActive: number;
  /** 集群同时打包数。 */
  concurrency: number;
  /** 集群等待任务容量。 */
  queueCapacity: number;
  /** 单轮后台执行秒数。 */
  executionSeconds: number;
  /** 每用户保留产物数，按完成时间排序。 */
  retainedCount: number;
  /** 完成后的最大保留天数。 */
  retentionDays: number;
  /** 可选文件类型由服务端与原归档类型统一提供；旧服务未返回时只提供原三种报告。 */
  fileTypes?: DownloadFileTypeOption[];
}

/** 服务端白名单编码；仅选择类型，不允许指定源文件ID、路径或签章事实。 */
export type ReportDownloadFormat = string;

/** 服务端提供的文件类型选项，包含原归档全部附件及审批记录。 */
export interface DownloadFileTypeOption {
  /** 提交时使用的稳定业务编码。 */
  value: ReportDownloadFormat;
  /** 展示名称，不用于拼接本地或服务器路径。 */
  label: string;
}

/** 后端升级前的兼容选择，不把未知类型提交给旧服务。 */
export const legacyDownloadFileTypes: DownloadFileTypeOption[] = [
  { label: 'PDF盖章报告', value: 'SIGNED_PDF' },
  { label: 'PDF未盖章报告', value: 'UNSIGNED_PDF' },
  { label: 'Word报告', value: 'WORD' },
];
/** 两个允许提交批量报告的页面。 */
export type DownloadSource = 'REPORT_REVIEW' | 'REPORT_STATISTICS';
/** 下载任务终态与可领取状态分开处理。 */
export type DownloadStatus = 'PENDING' | 'RUNNING' | 'SUCCESS' | 'PARTIAL_SUCCESS' | 'FAILED' | 'EXPIRED';

/** 下载中心任务，不包含存储桶、对象键、文件ID或登录凭据。 */
export interface DownloadTask {
  /** 下载任务ID，全程使用字符串。 */
  id: string;
  /** 业务来源页。 */
  source: DownloadSource;
  /** 数据库排队、处理或终态。 */
  status: DownloadStatus;
  /** 去重业务任务数。 */
  taskCount: number;
  /** 后台展开后的文件份数；排队阶段为所选任务和类型占位数。 */
  totalCount: number;
  /** 已处理文件份数。 */
  processedCount: number;
  /** 已装包文件份数。 */
  successCount: number;
  /** 失败文件份数。 */
  failureCount: number;
  /** 任务提交时间，使用项目服务端时间格式。 */
  createTime: string;
  /** 产物完成时间，重复下载不修改。 */
  finishedTime?: string;
  /** ZIP字节数，失败或未完成时为空。 */
  fileSize?: number | string;
  /** 完成后最长保留日期，超数量可提前清理。 */
  expireTime?: string;
  /** EXPIRED_BY_TIME或EXPIRED_BY_COUNT。 */
  cleanupReason?: string;
  /** 已脱敏的整包失败原因。 */
  errorMessage?: string;
}

/** 冻结文件清单，结果为文件级份数而非任务数。 */
export interface DownloadTaskItem {
  /** 明细ID。 */
  id: string;
  /** 业务任务ID，不是报告行ID。 */
  taskId: string;
  /** 报告、附件或审批记录类型编码。 */
  format: ReportDownloadFormat;
  /** 多套PDF用途，单套忽略。 */
  pdfVariant: 'BODY_ONLY' | 'FULL';
  /** 单份处理结果。 */
  status: 'PENDING' | 'READY' | 'SUCCESS' | 'FAILED';
  /** ZIP内已清理的相对文件名。 */
  fileName?: string;
  /** 源文件字节数。 */
  fileSize?: number | string;
  /** 失败原因，不含路径和报告内容。 */
  errorMessage?: string;
}

/** 标准分页响应。 */
export interface DownloadPage<T> {
  /** 本页结果。 */
  list: T[];
  /** 总记录数。 */
  total: number | string;
  /** 本人全部未完成任务数，仅任务列表返回，后端Long可能按字符串序列化。 */
  activeCount?: number | string;
}

/** 只包含业务选择的下载请求，禁止传入任意文件地址。 */
export interface ReportBatchRequest {
  /** 去重的业务任务字符串ID。 */
  taskIds: string[];
  /** 非空格式组合。 */
  formats: ReportDownloadFormat[];
  /** 多套PDF用途，默认BODY_ONLY。 */
  pdfVariant: 'BODY_ONLY' | 'FULL';
  /** 来源页面。 */
  source: DownloadSource;
  /** 同一次提交及其网络重试共用UUID。 */
  requestKey: string;
}

/** 解包项目标准响应；业务失败抛给页面，不伪装成空结果。 */
function unwrap<T>(response: any): T {
  if (response?.success === false) throw new Error(response.message || '下载请求失败');
  return (response?.result ?? response?.data ?? response) as T;
}

/** 当前有后台工作，只有这两种状态需要自动刷新。 */
export function isDownloadActive(status: DownloadStatus): boolean {
  return status === 'PENDING' || status === 'RUNNING';
}

/** 只允许领取已保存产物，不通过点击下载创建任务。 */
export function isDownloadAvailable(status: DownloadStatus): boolean {
  return status === 'SUCCESS' || status === 'PARTIAL_SUCCESS';
}

/** 生成UUID，在非HTTPS内网也使用浏览器密码学随机源。 */
export function downloadRequestKey(): string {
  if (globalThis.crypto.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** 通知已打开的下载中心刷新；不自动下载或打开抽屉。 */
export function notifyDownloadCreated(): void {
  window.dispatchEvent(new Event('download-center-changed'));
}

/** 在用户点击时打开统一下载中心，不离开业务页。 */
export function openDownloadCenter(): void {
  window.dispatchEvent(new Event('download-center-open'));
}

/** 下载中心API；创建、查询、重新打包与领取产物职责独立。 */
export const downloadTasksApi = {
  /** 获取服务端统一限制，不使用本地另一套容量默认值。 */
  async limits(): Promise<DownloadLimits> {
    return unwrap(await request('/admin/download-tasks/limits'));
  },
  /** 提交业务选择并立即返回任务，后台完成后才能领取。 */
  async create(data: ReportBatchRequest): Promise<DownloadTask> {
    return unwrap(await request('/admin/download-tasks/report-batch', { method: 'POST', data }));
  },
  /** 分页读取本人任务，禁止前端按任意用户查询。 */
  async list(page = 1, pageSize = 10): Promise<DownloadPage<DownloadTask>> {
    return unwrap(await request('/admin/download-tasks/list', { method: 'POST', data: { page, pageSize }, skipErrorHandler: true }));
  },
  /** 检查当前任务状态，文件清理后页面及时禁用领取。 */
  async detail(id: string): Promise<DownloadTask> {
    return unwrap(await request(`/admin/download-tasks/${encodeURIComponent(id)}`));
  },
  /**
   * 分页读取已鉴权的冻结文件结果，不传输源文件。
   * @param id 下载任务字符串ID @param page 从1开始的页码 @param pageSize 每页明细数，默认100
   * @returns 该页文件结果及总数；调用方需完整加载后再构造目录
   */
  async items(id: string, page = 1, pageSize = 100): Promise<DownloadPage<DownloadTaskItem>> {
    return unwrap(await request(`/admin/download-tasks/${encodeURIComponent(id)}/items`, { params: { page, pageSize } }));
  },
  /** 明确重新打包最新报告，网络重试必须复用调用方传入的键。 */
  async retry(id: string, requestKey: string): Promise<DownloadTask> {
    return unwrap(await request(`/admin/download-tasks/${encodeURIComponent(id)}/retry`, { method: 'POST', data: { requestKey } }));
  },
};

/**
 * 原生领取已有ZIP，使用当前Cookie会话，内存不接收整包Blob；后端领取时仍重新鉴权。
 * @param id 已完成的下载任务ID，绝不触发create或retry接口。
 */
export function downloadSavedArtifact(id: string): void {
  const link = document.createElement('a');
  link.href = buildApiPath(`/admin/download-tasks/${encodeURIComponent(id)}/download`);
  // 文件名由成功响应的Content-Disposition提供，鉴权JSON错误不能被强制保存成伪ZIP。
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
}
